import { call, put, fork, take, all, select } from 'redux-saga/effects';
import ActionTypes from './constants';
import MainActionTypes from 'containers/MainLayout/constants';
import { refreshCart, updateItemCart } from 'containers/MainLayout/actions';
import { removeCartItemLight, removeCartSupplierLight, getBuyer } from 'utils/apollo';
import { flatMap } from 'lodash';
import { getStore } from 'containers/MainLayout/saga';
import { selectCart } from 'containers/MainLayout/selectors';
import { CartItem } from 'types/schema';
import { KAMEREO_SUPPLIER_ID } from 'utils/constants';
import { setCheckout, setCheckoutTotal } from 'containers/CheckoutPage/actions';
import { push } from 'connected-react-router';
import { fetchFrequentlyBuyTogetherProduct } from 'components/FrequentlyBuyTogether/actions';
import calculateVAT from 'utils/calculateVAT';

export function computeCheckoutQuantity(cartSet: any) {
  return flatMap(cartSet, (singleSupplierCart) => singleSupplierCart.items).reduce((agg, item) => {
    agg[item.product.id] = Number(Number(item.quantity).toFixed(3));
    return agg;
  }, {});
}

export function computeCheckoutSupplierTotals(cartSet: any) {
  return cartSet.reduce((agg, singleSupplierCart) => {
    agg[singleSupplierCart.supplier.id] = singleSupplierCart.total;
    return agg;
  }, {});
}

export function computeCheckoutSupplierTotalBeforeVAT(cartSet: any) {
  return cartSet.reduce((agg, singleSupplierCart) => {
    const totalBeforeVAT = singleSupplierCart.items.reduce((acc, item) => {
      const totalBeforeVATofItem =
        calculateVAT(item?.product?.priceV2?.price, item?.product?.priceV2?.vat, item?.product?.priceV2?.vatIncluded)
          .beforeVat * item?.quantity;
      return acc + totalBeforeVATofItem;
    }, 0);
    agg[singleSupplierCart.supplier.id] = totalBeforeVAT;
    return agg;
  }, {});
}

export function computeCheckoutCartTotal(cartSet: any) {
  return cartSet.reduce((total, singleSupplierCart) => total + singleSupplierCart.total, 0);
}

export function computeCheckoutQuantityAndTotal(cartSet: any) {
  return {
    quantity: computeCheckoutQuantity(cartSet),
    totalOfSupplier: computeCheckoutSupplierTotals(cartSet),
    totalBeforeVATOfSupplier: computeCheckoutSupplierTotalBeforeVAT(cartSet),
    total: computeCheckoutCartTotal(cartSet),
  };
}

export function* checkStore(store) {
  const buyer = yield call(getBuyer);
  if (buyer?.id) {
    const isStoreAvailable = buyer?.stores?.some((storeEl) => storeEl?.id === store?.id);
    if (!isStoreAvailable) {
      yield put(push('/change-store'));
    }
  }
}

function* initCartFlow() {
  let store = yield call(getStore);
  if (!store) {
    yield take(MainActionTypes.SET_SELECTED_STORE);
    store = yield call(getStore);
  }

  // yield put(refreshCart());
  yield take(MainActionTypes.FETCH_CART_SUCCESS);

  const cart = yield select(selectCart());
  const checkoutQuantityAndTotalResult = computeCheckoutQuantityAndTotal(cart.cartSet);
  yield put(setCheckout(checkoutQuantityAndTotalResult));
  yield put(setCheckoutTotal(checkoutQuantityAndTotalResult.total));

  yield put(fetchFrequentlyBuyTogetherProduct({}));
  if (cart.notAvailableProducts.length > 0) {
    yield all(
      cart.notAvailableProducts.map((item: CartItem) => {
        return call(removeCartItemLight, {
          input: { storeId: store.id, supplierId: KAMEREO_SUPPLIER_ID, productId: item?.product?.id },
        });
      }),
    );
  }

  // check deactivated store
  yield call(checkStore, store);
}

function* initCheckoutStep1Flow() {
  yield call(initCartFlow);
}

function* removeAllProductFlow(action) {
  const { payload } = action;
  const store = yield call(getStore);
  if (!store) {
    return;
  }
  const response = yield call(removeCartSupplierLight, {
    storeId: store.id,
    ...payload,
  });
  if (!response.errors) {
    yield put(updateItemCart.success(response.cart));
  }
}
export function* updateCartSuccess() {
  yield put(refreshCart());
  yield call(initCartFlow);
}
export default function* checkoutStep1PageSaga() {
  yield all(
    [
      [ActionTypes.REMOVE_ALL_PRODUCT, removeAllProductFlow],
      [MainActionTypes.UPDATE_CART_SUCCESS, updateCartSuccess],
    ].map(([actionName, handler]) =>
      fork(function* () {
        while (true) {
          const action = yield take(actionName as any);
          yield call(handler as any, action as any);
        }
      }),
    ),
  );
  yield fork(initCheckoutStep1Flow);
}
